import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  CardTravel as CardTravelIcon,
  Public as PublicIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';

const SupplierDetails = ({ supplier, onClose }) => {
  const { formatCurrency, formatDate } = useSettings();
  
  if (!supplier) return null;
  
  // Check if a field exists and is not empty
  const hasField = (field) => {
    return supplier[field] !== undefined && supplier[field] !== null && supplier[field] !== '';
  };
  
  // These are the only fields that are part of the supplier creation form
  const formFields = [
    'nom', 'type', 'pays', 'ville', 'email', 'telephone', 'adresse', 
    'personneContact', 'statut', 'commission', 'delaiPaiement', 'description'
  ];
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Supplier Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar 
          sx={{ 
            width: 72, 
            height: 72, 
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
          }}
        >
          {supplier.nom?.charAt(0) || 'S'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
            {supplier.nom}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            {hasField('type') && (
              <Chip 
                icon={<BusinessIcon />} 
                label={supplier.type} 
                size="small" 
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}
              />
            )}
            {(hasField('ville') || hasField('pays')) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {[supplier.ville, supplier.pays].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {hasField('statut') && (
          <Chip 
            label={supplier.statut} 
            size="small"
            sx={{ 
              borderRadius: 2,
              backgroundColor: supplier.statut === 'Actif' 
                ? 'rgba(34, 197, 94, 0.2)' 
                : supplier.statut === 'Suspendu' 
                ? 'rgba(245, 158, 11, 0.2)' 
                : 'rgba(239, 68, 68, 0.2)',
              color: supplier.statut === 'Actif' 
                ? '#4ade80' 
                : supplier.statut === 'Suspendu' 
                ? '#fbbf24' 
                : '#f87171',
              border: `1px solid ${supplier.statut === 'Actif' 
                ? 'rgba(34, 197, 94, 0.3)' 
                : supplier.statut === 'Suspendu' 
                ? 'rgba(245, 158, 11, 0.3)' 
                : 'rgba(239, 68, 68, 0.3)'}`
            }}
          />
        )}
      </Box>
      
      <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      
      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Contact & Details */}
        <Grid item xs={12} md={6}>
          {/* Only show contact info section if at least one contact field exists */}
          {(hasField('email') || hasField('telephone') || hasField('adresse') || hasField('personneContact')) && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Informations de Contact
              </Typography>
              
              <List disablePadding>
                {hasField('email') && (
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <EmailIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={supplier.email}
                      primaryTypographyProps={{ variant: 'body2', sx: { color: 'white' } }}
                    />
                  </ListItem>
                )}
                
                {hasField('telephone') && (
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <PhoneIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={supplier.telephone}
                      primaryTypographyProps={{ variant: 'body2', sx: { color: 'white' } }}
                    />
                  </ListItem>
                )}
                
                {hasField('adresse') && (
                  <ListItem disablePadding sx={{ mb: 1.5 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <LocationIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={supplier.adresse}
                      primaryTypographyProps={{ variant: 'body2', sx: { color: 'white' } }}
                    />
                  </ListItem>
                )}
                
                {hasField('personneContact') && (
                  <ListItem disablePadding>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <PersonIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={supplier.personneContact}
                      secondary="Personne de contact"
                      primaryTypographyProps={{ variant: 'body2', sx: { color: 'white' } }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}
          
          {/* Only show commercial terms if at least one field exists */}
          {(hasField('commission') || hasField('delaiPaiement')) && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Termes Commerciaux
              </Typography>
              
              <Grid container spacing={2}>
                {hasField('commission') && (
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Commission
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4ade80' }}>
                      {supplier.commission}%
                    </Typography>
                  </Grid>
                )}
                
                {hasField('delaiPaiement') && (
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Délai de Paiement
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                      {supplier.delaiPaiement} jours
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
          
          {hasField('description') && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {supplier.description}
              </Typography>
            </Paper>
          )}
        </Grid>
        
        {/* Right Column - Only show related packages if they exist */}
        <Grid item xs={12} md={6}>
          {supplier.forfaitsGeres && supplier.forfaitsGeres.length > 0 && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Forfaits Associés
              </Typography>
              
              <List disablePadding>
                {supplier.forfaitsGeres.map((forfait, index) => (
                  <React.Fragment key={forfait.id || index}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <CardTravelIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} fontSize="small" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={forfait.nom}
                        secondary={formatCurrency(forfait.prix || 0)}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500, sx: { color: 'white' } }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                      />
                    </ListItem>
                    {index < supplier.forfaitsGeres.length - 1 && <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }} />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupplierDetails; 